/**
 * Pipeline Orchestrator — Runs all validation stages and produces final verdict.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.9
 *
 * Pipeline flow:
 *   Question → Schema(1) → Graph(2) → Content(3) → Visual(4) → Support(5) → Score(6) → Gate(7) → Verdict
 *
 * Design principles:
 * - N4: Issue Contract governs gates; scoring never overrides blockers
 * - N5: Issue determinism — same input → same issues
 * - N6: No silent data correction in production
 */

import type { ValidationIssue, PipelineStage, PipelineResult, PipelineBatchResult, GateResult, IssueDomain, GateVerdict, IssueKind } from './issueContract';
import { evaluateGate, deduplicateIssues, DEFAULT_GATE_THRESHOLDS, computeIssuePenalty } from './issueContract';
import { getGateThresholds, getScoringWeights, isAssessmentEligibleType } from './policyEngine';
import { applyEnforcementModes, deriveModuleFromFamily } from './applyEnforcementModes';
import { error as logError } from '../../utils/logger';
import type { GovernanceConfigOverrides } from './governanceConfigStore';
import { recordShadowFinding, type ShadowFinding } from '../shadow/shadowFindings';
import { runSchemaStage } from './stages/schemaStage';
import { runTypeStage } from './stages/typeStage';
import { runContentStage } from './stages/contentStage';
import { runVisualStage, runVisualConsistencyBatch } from './stages/visualStage';
import { runSupportStage } from './stages/supportStage';
import { runGraphStage } from './stages/graphStage';
import { runSeedStage } from './stages/seedStage';
import { runPedagogyStage } from './stages/pedagogyStage';
import { runDiversityBatch } from './stages/diversityStage';
import { runAssessmentHealthBatch } from './stages/assessmentHealthStage';
import { STAGES } from './stageDefinitions';
import type { LDGraph } from '../ldg/types';
import type { Question } from '../../content';

// ─── Pipeline Configuration ───────────────────────────────────────────────────

export interface PipelineOptions {
  /** Built LDG graph for Stage 2 validation */
  graph?: LDGraph;
  /** Skip specific stages (for performance or partial runs) */
  skipStages?: PipelineStage[];
  /** Override gate thresholds */
  thresholds?: typeof DEFAULT_GATE_THRESHOLDS;
  /** Skip pedagogy stage independently of content stage */
  skipPedagogy?: boolean;
  /**
   * FG4 §Fase A — Resolved governance config (per-module / per-type
   * enforcement modes + grace ledger). When supplied, the orchestrator
   * partitions deduped issues via applyEnforcementModes:
   *   off → drop, shadow → record + drop, warn → downgrade blocker→coach,
   *   enforce → keep (with optional grace-ledger severity degrade).
   * When undefined, behaviour is identical to pre-FG4 (no ramp).
   */
  enforcementConfig?: GovernanceConfigOverrides;
  /**
   * FG4 §Fase A — Optional shadow sink override. Defaults to the module-level
   * `recordShadowFinding` store. Tests and dry-run tooling inject their own
   * recorder to avoid polluting the live findings store.
   */
  shadowSink?: (finding: Omit<ShadowFinding, 'detectedAt'>) => void;
}

// ─── Stage Runner Helper (C4, Sprint 3) ──────────────────────────────────────

/**
 * Shared stage execution wrapper.
 *
 * RESOLVES: C4 — collapses 8× duplicate try/catch blocks into one helper with
 * identical behavior. Each stage either replaces or appends to its bucket in
 * `stageResults`, and errors are concatenated when the same stage runs twice
 * (Type→1, Pedagogy→3, Seed→5).
 */
interface StageCtx {
  stageResults: Record<PipelineStage, ValidationIssue[]>;
  allIssues: ValidationIssue[];
  stageErrors: Partial<Record<PipelineStage, string>>;
}
function runStage(ctx: StageCtx, spec: {
  stage: PipelineStage;
  enabled: boolean;
  append?: boolean;
  run: () => ValidationIssue[];
}): void {
  if (!spec.enabled) return;
  try {
    const issues = spec.run();
    if (spec.append) {
      ctx.stageResults[spec.stage] = [...(ctx.stageResults[spec.stage] ?? []), ...issues];
    } else {
      ctx.stageResults[spec.stage] = issues;
    }
    ctx.allIssues.push(...issues);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    ctx.stageErrors[spec.stage] = ctx.stageErrors[spec.stage] ? ctx.stageErrors[spec.stage] + '; ' + msg : msg;
  }
}

// ─── Single Question Pipeline ─────────────────────────────────────────────────

/**
 * Run the full governance pipeline on a single question.
 *
 * This is the PRIMARY validation entry point for the v4.0 architecture.
 * It replaces the old `validateQuestion` in `unifiedValidator.ts`.
 */
export function runPipeline(question: Question, options?: PipelineOptions): PipelineResult {
  const skipStages = new Set(options?.skipStages ?? []);
  const ctx: StageCtx = {
    allIssues: [],
    stageResults: {
      [STAGES.SCHEMA]: [],
      [STAGES.GRAPH]: [],
      [STAGES.CONTENT]: [],
      [STAGES.VISUAL]: [],
      [STAGES.SUPPORT]: [],
      [STAGES.SCORING]: [],
      [STAGES.GATE]: []
    } as Record<PipelineStage, ValidationIssue[]>,
    stageErrors: {}
  };

  // Stage 1: Schema Validation
  runStage(ctx, {
    stage: STAGES.SCHEMA,
    enabled: !skipStages.has(STAGES.SCHEMA),
    run: () => runSchemaStage(question)
  });

  // Stage 1b: Question Type Validation (shares stage 1 bucket)
  runStage(ctx, {
    stage: STAGES.TYPE,
    enabled: !skipStages.has(STAGES.TYPE),
    append: true,
    run: () => runTypeStage(question)
  });

  // Stage 2: Graph Validation
  runStage(ctx, {
    stage: STAGES.GRAPH,
    enabled: !skipStages.has(STAGES.GRAPH),
    run: () => runGraphStage(question, options?.graph)
  });

  // Stage 3: Content Validation
  runStage(ctx, {
    stage: STAGES.CONTENT,
    enabled: !skipStages.has(STAGES.CONTENT),
    run: () => runContentStage(question)
  });

  // Stage 3b: Pedagogy Validation (shares stage 3 bucket)
  runStage(ctx, {
    stage: STAGES.PEDAGOGY,
    enabled: !skipStages.has(STAGES.PEDAGOGY) && !options?.skipPedagogy,
    append: true,
    run: () => runPedagogyStage(question)
  });

  // Stage 4: Visual Validation
  runStage(ctx, {
    stage: STAGES.VISUAL,
    enabled: !skipStages.has(STAGES.VISUAL),
    run: () => runVisualStage(question)
  });

  // Stage 5: Support Validation
  runStage(ctx, {
    stage: STAGES.SUPPORT,
    enabled: !skipStages.has(STAGES.SUPPORT),
    run: () => runSupportStage(question)
  });

  // Stage 5b: Seed Consolidation Warnings — always runs (data integrity)
  runStage(ctx, {
    stage: STAGES.SEED,
    enabled: true,
    append: true,
    run: () => runSeedStage(question)
  });
  const {
    allIssues,
    stageResults,
    stageErrors
  } = ctx;

  // ── Stage 6: Scoring (computed in gate evaluation) ───────────────────────
  // No separate scoring stage — score is computed as part of gate evaluation.
  // This keeps scoring deterministic and tied to the issue set.

  // ── Stage 7: Gate Logic ──────────────────────────────────────────────────

  const dedupedIssues = deduplicateIssues(allIssues);

  // ── FG4 Fase A — Enforcement-mode partition (shadow / warn / enforce) ───
  // No-op when options.enforcementConfig is undefined: retainedIssues stays
  // === dedupedIssues and no findings are emitted. Strictly additive.
  const sinkFn = options?.shadowSink ?? recordShadowFinding;
  const enforcement = applyEnforcementModes({
    issues: dedupedIssues,
    config: options?.enforcementConfig,
    shadowSink: {
      record: f => sinkFn(f)
    },
    module: deriveModuleFromFamily(question.familyId),
    questionId: question.id ?? 'UNKNOWN',
    family: question.familyId ?? 'UNKNOWN',
    tier: question.tier ?? 0,
    questionType: question.questionType
  });
  const gateInputIssues = enforcement.retainedIssues;
  const thresholds = options?.thresholds ?? getGateThresholds();
  const gateResult = evaluateGate(gateInputIssues, thresholds);

  // Override assessment eligibility based on question type
  if (question.questionType && !isAssessmentEligibleType(question.questionType)) {
    gateResult.assessmentEligible = false;
  }

  // If any stage errored, force REVIEW_NEEDED so failures are never silent
  const failedStages = Object.keys(stageErrors).map(Number);
  if (failedStages.length > 0 && gateResult.verdict === 'PASS') {
    gateResult.verdict = 'REVIEW_NEEDED';
    gateResult.reviewNeeded = true;
    gateResult.reason = `Stage(s) ${failedStages.join(', ')} threw runtime error(s) — review needed`;
  }

  // ── Compute domain scores ────────────────────────────────────────────────

  const domainScores = computeDomainScores(dedupedIssues);
  return {
    questionId: question.id ?? 'UNKNOWN',
    familyId: question.familyId ?? 'UNKNOWN',
    gate: gateResult,
    stageResults,
    domainScores,
    ...(failedStages.length > 0 ? {
      stageErrors
    } : {}),
    timestamp: Date.now()
  };
}

// ─── Domain Score Computation ───────────────────────────────────────────────

const ALL_DOMAINS: IssueDomain[] = ['schema', 'type', 'graph', 'content', 'pedagogy', 'visual', 'support', 'familyStats', 'diversity', 'seed', 'scoring', 'assessmentPool', 'gate'];

/**
 * Compute per-domain quality scores from deduplicated issues.
 * Each domain starts at 100 and loses points based on issue penalties.
 */
function computeDomainScores(issues: ValidationIssue[]): Record<IssueDomain, number> {
  const scores = {} as Record<IssueDomain, number>;
  // Phase 4: read scoring weights once per call rather than per-issue.
  const weights = getScoringWeights();
  for (const domain of ALL_DOMAINS) {
    const domainIssues = issues.filter(i => i.domain === domain);
    const penalty = computeIssuePenalty(domainIssues, weights);
    scores[domain] = Math.max(0, 100 - penalty);
  }
  return scores;
}

// ─── Batch Pipeline ─────────────────────────────────────────────────────────

/**
 * Run the governance pipeline on a batch of questions.
 *
 * This is the main entry point for the admin governance dashboard.
 * It runs runPipeline on each question and aggregates the results.
 */
export function runPipelineBatch(questions: Question[], options?: PipelineOptions): PipelineBatchResult {
  // Per vraag geïsoleerd. `questions.map((q) => runPipeline(q, options))` liet
  // één onverwachte fout de HELE batch omvallen: de beheerder kreeg dan geen
  // resultaat voor duizenden vragen omdat er één stuk was. De per-stage
  // try/catch dekt dat niet — alles buiten de stages (gate-evaluatie,
  // domeinscores, enforcement) valt erbuiten.
  //
  // Een vraag die niet te controleren is, verdwijnt hier bewust NIET stil: hij
  // ontbreekt in de resultaten en wordt luid gelogd met zijn id. "Niet
  // gecontroleerd" mag nooit als "goedgekeurd" doorgaan, en een lege
  // resultatenlijst is het enige eerlijke antwoord voor een vraag waarover we
  // niets weten.
  const results: PipelineResult[] = [];
  const crashedQuestionIds: string[] = [];
  for (const q of questions) {
    try {
      results.push(runPipeline(q, options));
    } catch (err) {
      crashedQuestionIds.push(q.id);
      logError(`[Governance] Pipeline crashte op vraag ${q.id}; deze vraag is NIET gevalideerd ` + 'en ontbreekt in het rapport. De overige vragen zijn wel gecontroleerd.', err);
    }
  }
  if (crashedQuestionIds.length > 0) {
    logError(`[Governance] ${crashedQuestionIds.length} van ${questions.length} vragen konden niet ` + `gevalideerd worden: ${crashedQuestionIds.slice(0, 20).join(', ')}` + (crashedQuestionIds.length > 20 ? ', …' : '') + '. Behandel het rapport als onvolledig.');
  }

  // C10 (Sprint 3): index results by questionId once so cross-batch merges
  // are O(N) instead of O(batch_issues × N). At 263 questions × ~50 cross-issues
  // this matters for admin dashboard responsiveness.
  const resultsByQuestionId = new Map<string, PipelineResult>();
  for (const r of results) resultsByQuestionId.set(r.questionId, r);
  const mergeCrossBatchIssue = (issue: ValidationIssue): void => {
    if (!issue.questionId) return;
    const target = resultsByQuestionId.get(issue.questionId);
    if (!target) return;
    target.gate.issues.push(issue);
    target.gate.counts.total += 1;
    if (issue.kind === 'blocker') target.gate.counts.blockers += 1;else if (issue.kind === 'coach') target.gate.counts.coaches += 1;else target.gate.counts.infos += 1;
  };

  // Run cross-question visual consistency checks
  const visualConsistencyIssues = runVisualConsistencyBatch(questions);
  for (const issue of visualConsistencyIssues) mergeCrossBatchIssue(issue);

  // Run cross-question diversity checks (family-level type diversity)
  const diversityIssues = runDiversityBatch(questions);
  for (const issue of diversityIssues) mergeCrossBatchIssue(issue);

  // S8 / FG-AssessmentPool — Layer A capacity check.
  // Batch-only, diagnostic-only (coach/info, never blocker), runs after
  // all per-question stages and after diversity so it sees the final
  // question set. Domain 'assessmentPool' keeps these findings out of
  // existing dashboards by default — they surface in the dedicated
  // Assessment Health admin tab (added in a follow-up step).
  const assessmentHealthIssues = runAssessmentHealthBatch(questions);
  for (const issue of assessmentHealthIssues) mergeCrossBatchIssue(issue);

  // Aggregate verdicts
  const verdicts: Record<GateVerdict, number> = {
    PASS: 0,
    FAIL: 0,
    REVIEW_NEEDED: 0
  };
  let totalScore = 0;
  let productionCount = 0;
  let assessmentCount = 0;
  let reviewCount = 0;
  const issuesByDomain = {} as Record<IssueDomain, number>;
  for (const d of ALL_DOMAINS) issuesByDomain[d] = 0;
  const issuesByKind: Record<IssueKind, number> = {
    blocker: 0,
    coach: 0,
    info: 0
  };
  for (const r of results) {
    verdicts[r.gate.verdict] += 1;
    totalScore += r.gate.score;
    if (r.gate.productionEligible) productionCount += 1;
    if (r.gate.assessmentEligible) assessmentCount += 1;
    if (r.gate.reviewNeeded) reviewCount += 1;
    for (const issue of r.gate.issues) {
      issuesByDomain[issue.domain] = (issuesByDomain[issue.domain] || 0) + 1;
      issuesByKind[issue.kind] += 1;
    }
  }
  return {
    generatedAt: new Date().toISOString(),
    totalQuestions: questions.length,
    verdicts,
    productionEligible: productionCount,
    assessmentEligible: assessmentCount,
    reviewNeeded: reviewCount,
    avgScore: results.length > 0 ? Math.round(totalScore / results.length) : 0,
    issuesByDomain,
    issuesByKind,
    results
  };
}